import React, { createContext, useContext, useEffect, useState } from 'react';
import {
    LEPDashboardFilter,
    LEPDistributionDto,
    LEPYearTotalDto,
    LEPGrandTotalDto,
} from 'generated/openApi';
import ApiService from 'services/apiService';

interface LEPDataContextType {
    data: LEPDistributionDto[];
    loading: boolean;
    error: string | null;
    filter: LEPDashboardFilter;
    activeSchoolYear: number | null;
    totalsByYear: LEPYearTotalDto[];
    grandTotal: LEPGrandTotalDto | null;
    updateFilter: (filter: Partial<LEPDashboardFilter>) => void;
    refetch: () => void;
}

const LEPDataContext = createContext<LEPDataContextType | undefined>(undefined);

export const LEPDataProvider = ({ children }: { children: React.ReactNode }) => {
    const [data, setData] = useState<LEPDistributionDto[]>([]);
    const [totalsByYear, setTotalsByYear] = useState<LEPYearTotalDto[]>([]);
    const [grandTotal, setGrandTotal] = useState<LEPGrandTotalDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeSchoolYear, setActiveSchoolYear] = useState<number | null>(null);
    const [filter, setFilter] = useState<LEPDashboardFilter>({
        schoolYears: [],
    });
    const [initialized, setInitialized] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const responseData = await ApiService.lepDashboard.getLEPDistribution(filter);

            if (responseData.activeSchoolYear && !activeSchoolYear) {
                setActiveSchoolYear(responseData.activeSchoolYear);

                if (!initialized && filter.schoolYears.length === 0) {
                    setFilter(prev => ({
                        ...prev,
                        schoolYears: [responseData.activeSchoolYear]
                    }));
                    setInitialized(true);
                    return;
                }
            }

            setData(responseData.data || []);
            setTotalsByYear(responseData.totalsByYear || []);
            setGrandTotal(responseData.grandTotal || null);
        } catch (err) {
            setError('Failed to load LEP data');
            console.error('Error fetching LEP data:', err);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (initialized) {
            fetchData();
        }
    }, [filter]);

    const updateFilter = (updates: Partial<LEPDashboardFilter>) => {
        setFilter(prev => ({ ...prev, ...updates }));
    };

    const refetch = () => {
        fetchData();
    };

    return (
        <LEPDataContext.Provider
            value={{
                data,
                loading,
                error,
                filter,
                activeSchoolYear,
                totalsByYear,
                grandTotal,
                updateFilter,
                refetch,
            }}
        >
            {children}
        </LEPDataContext.Provider>
    );
};

export const useLEPData = () => {
    const context = useContext(LEPDataContext);
    if (!context) {
        throw new Error('useLEPData must be used within LEPDataProvider');
    }
    return context;
};